import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAdminI18n } from '../../../lib/i18n/use-admin-i18n';
import { useAuthStore } from '../../../stores/authStore';
import { withAreaPrefix } from '../../../lib/area-routes';

interface QuickLinkDef {
  readonly id: 'users' | 'my_product' | 'projects';
  readonly path: string;
  readonly requiredScope?: string;
  readonly requiredAnyScopes?: string[];
  readonly icon: string;
}

function quickLinkVisible(item: QuickLinkDef, hasScope: (s: string) => boolean): boolean {
  if (item.requiredAnyScopes?.length) {
    return item.requiredAnyScopes.some(s => hasScope(s));
  }
  if (!item.requiredScope) return true;
  return hasScope(item.requiredScope);
}

const QUICK_LINK_DEFS: QuickLinkDef[] = [
  {
    id: 'users',
    path: '/tenant/users',
    requiredScope: 'tenant-user:read',
    icon: '👥',
  },
  {
    id: 'my_product',
    path: '/tenant/my-product',
    requiredAnyScopes: ['product:read', 'plan:read'],
    icon: '📦',
  },
  {
    id: 'projects',
    path: '/tenant/projects',
    requiredScope: 'project:read',
    icon: '🧩',
  },
];

/** Home dashboard for non–platform (tenant) users at `/dashboard`. */
export function TenantAreaDashboardPage() {
  const { page, language } = useAdminI18n();
  const user = useAuthStore(state => state.user);
  const hasScope = useAuthStore(state => state.hasScope);

  const displayName =
    user?.name?.trim() || user?.email?.split('@')[0] || page('tenant_dashboard', 'user_fallback');

  const visibleLinks = useMemo(
    () =>
      QUICK_LINK_DEFS.filter(item => quickLinkVisible(item, hasScope)).map(item => ({
        ...item,
        title: page('tenant_dashboard', `shortcuts.${item.id}.title`),
        description: page('tenant_dashboard', `shortcuts.${item.id}.description`),
      })),
    [hasScope, page, language],
  );

  return (
    <div className="min-h-full space-y-6 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          {page('tenant_dashboard', 'welcome', { name: displayName })}
        </h1>
        <p className="text-muted-foreground text-sm md:text-base">
          {page('tenant_dashboard', 'subtitle')}
        </p>
      </header>

      <section className="rounded-xl border bg-card p-5">
        <h2 className="text-base font-semibold text-foreground">
          {page('tenant_dashboard', 'workspace_title')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {page('tenant_dashboard', 'workspace_description')}
        </p>
      </section>

      <section>
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          {page('tenant_dashboard', 'quick_access')}
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {visibleLinks.map(item => (
            <Link
              key={`${item.path}-${item.id}`}
              to={withAreaPrefix(item.path, globalThis.location.pathname)}
              className="group flex items-start gap-4 rounded-xl border border-border bg-card p-5 transition-all duration-200 hover:border-primary/30 hover:bg-muted/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 focus:ring-offset-background"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xl text-primary transition-colors group-hover:bg-primary/20">
                {item.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-foreground transition-colors group-hover:text-primary">
                  {item.title}
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">{item.description}</p>
              </div>
              <span
                className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary"
                aria-hidden
              >
                →
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
